import { Lock } from "lucide-react";
import { type TeamAnalysisSummary, type TeamAnalysisHomeAway, type TopPlayer } from "@/api/tournamentAnalysis";
import { Loader } from "../../common/loader";
import { generateTournamentAchievements, type Achievement } from "./TournamentAnalysisAchievementsDefinition";
import { useSelectedTeam } from "@/context/teamContext";

interface TournamentAnalysisAchievementsProps {
    summaryData?: TeamAnalysisSummary;
    homeAwayData?: TeamAnalysisHomeAway;
    topPlayers?: TopPlayer[];
    isLoading?: boolean;
}

const rarityConfig = {
    common: {
        badge: "rarity-common-badge",
        icon: "rarity-common-icon",
    },
    rare: {
        badge: "rarity-rare-badge",
        icon: "rarity-rare-icon",
    },
    epic: {
        badge: "rarity-epic-badge",
        icon: "rarity-epic-icon",
    },
    legendary: {
        badge: "rarity-legend-badge",
        icon: "rarity-legend-icon",
    },
};

const UnlockedRow = ({ achievement, index }: { achievement: Achievement; index: number }) => {
    const cfg = rarityConfig[achievement.rarity];
    const rowBg = index % 2 === 0 ? "bg-surface-container-lowest" : "bg-surface-container-low";
    return (
        <div
            className={`data-table-row grid grid-cols-[auto_1fr] gap-4 items-center px-6 py-3 hover:bg-surface-container ${rowBg}`}
        >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${cfg.icon}`}>
                {achievement.icon}
            </div>
            <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm text-on-surface">{achievement.name}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${cfg.badge}`}>
                        {achievement.rarity}
                    </span>
                </div>
                <p className="text-xs text-on-surface-variant mt-0.5 line-clamp-1">{achievement.description}</p>
            </div>
        </div>
    );
};

const LockedRow = ({ achievement, index }: { achievement: Achievement; index: number }) => {
    const rowBg = index % 2 === 0 ? "bg-surface-container-lowest" : "bg-surface-container-low";
    return (
        <div className={`data-table-row grid grid-cols-[auto_1fr] gap-4 items-center px-6 py-3 opacity-40 ${rowBg}`}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-surface-container-highest text-on-surface-variant">
                <Lock className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm text-on-surface-variant">{achievement.name}</span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-surface-container-highest text-on-surface-variant">
                        {achievement.rarity}
                    </span>
                </div>
                <p className="text-xs text-on-surface-variant mt-0.5 line-clamp-1">{achievement.description}</p>
            </div>
        </div>
    );
};

export const TournamentAnalysisAchievements = ({
    summaryData,
    homeAwayData,
    topPlayers,
    isLoading,
}: TournamentAnalysisAchievementsProps) => {
    const { teamName } = useSelectedTeam();
    const rarityOrder = { legendary: 0, epic: 1, rare: 2, common: 3 };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center" style={{ minHeight: "300px" }}>
                <Loader message="Loading achievements..." />
            </div>
        );
    }

    if (!summaryData || !homeAwayData) {
        return (
            <div className="flex items-center justify-center" style={{ minHeight: "300px" }}>
                <p className="text-on-surface-variant">No achievement data available</p>
            </div>
        );
    }

    const achievements = generateTournamentAchievements(
        homeAwayData.teamStats,
        summaryData.matchSummary,
        homeAwayData.homeAwayComparison,
        topPlayers ?? [],
        teamName ?? ""
    );
    const unlocked = achievements
        .filter((a) => a.unlocked)
        .sort((a, b) => rarityOrder[a.rarity] - rarityOrder[b.rarity]);
    const locked = achievements.filter((a) => !a.unlocked);

    return (
        <div className="flex flex-col">
            <div className="shrink-0">
                <h3 className="px-4 pt-3 pb-3 text-sm font-headline font-bold section-title">
                    Tournament Achievements
                    <span className="ml-2 text-sm font-normal text-on-surface-variant">
                        {unlocked.length}/{achievements.length} unlocked
                    </span>
                </h3>
            </div>

            <div
                id="onboarding-tournament-achievements"
                className="overflow-auto rounded-xl data-table-wrapper md:max-h-[320px]"
            >
                <div className="data-table-header grid grid-cols-[auto_1fr] gap-4 px-6 py-3 sticky top-0 z-10">
                    <span className="font-headline font-black text-[10px] uppercase tracking-widest">Badge</span>
                    <span className="font-headline font-black text-[10px] uppercase tracking-widest">Achievement</span>
                </div>

                <div className="data-table-body">
                    {unlocked.map((a, index) => (
                        <UnlockedRow key={a.id} achievement={a} index={index} />
                    ))}
                    {locked.map((a, lockedIndex) => (
                        <LockedRow key={a.id} achievement={a} index={unlocked.length + lockedIndex} />
                    ))}
                </div>
            </div>
        </div>
    );
};
